import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import ImportarRoteiroDialog from "@/components/ImportarRoteiroDialog";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Building2,
  Plus,
  Edit,
  Copy,
  Trash2,
  Search,
  Save,
  Loader2,
  ArrowLeft,
  Calculator,
  DollarSign,
  FileText,
  Settings,
  Percent,
  ClipboardList,
  X,
  Check,
  Landmark,
  Upload,
  FileSpreadsheet,
  Eye,
  EyeOff,
  ExternalLink
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";

// Dados iniciais de tabela de fatores
const fatoresIniciais = [
  { id: "1", prazo: 12, fator: 0.0925, taxa: 2.05 },
  { id: "2", prazo: 24, fator: 0.0512, taxa: 1.95 },
  { id: "3", prazo: 36, fator: 0.0375, taxa: 1.85 },
  { id: "4", prazo: 48, fator: 0.0305, taxa: 1.75 },
  { id: "5", prazo: 60, fator: 0.0262, taxa: 1.70 },
  { id: "6", prazo: 72, fator: 0.0233, taxa: 1.65 },
  { id: "7", prazo: 84, fator: 0.0210, taxa: 1.60 },
  { id: "8", prazo: 96, fator: 0.0192, taxa: 1.55 },
];

// Dados iniciais de comissões com modelo de cálculo
const comissoesIniciais = [];

// Modelos de cálculo disponíveis
const MODELOS_CALCULO = [
  { value: "VALOR_CONTRATO", label: "V.Contrato" },
  { value: "COMISSAO_EMPRESA", label: "Com.Emp" }
];

// Tipos de operação
const TIPOS_OPERACAO = [
  "EMPRÉSTIMO NOVO",
  "REFINANCIAMENTO",
  "PORTABILIDADE",
  "CARTÃO CONSIGNADO",
  "CARTÃO BENEFÍCIO",
  "SAQUE FGTS",
  "SAQUE ANIVERSÁRIO",
  "DBT"
];

// Dados iniciais de roteiros operacionais
const roteirosIniciais = [
  { id: "1", titulo: "Documentação Exigida", descricao: "RG, CPF, Comprovante de Residência, Contra Cheque (3 últimos)", ativo: true, instituicao: "", tiposOperacao: [] },
  { id: "2", titulo: "Prazo de Análise", descricao: "24 a 48 horas úteis após envio da documentação", ativo: true, instituicao: "", tiposOperacao: [] },
  { id: "3", titulo: "Canal de Formalização", descricao: "Portal do Banco ou WhatsApp Institucional", ativo: true, instituicao: "", tiposOperacao: [] },
  { id: "4", titulo: "Idade Mínima", descricao: "18 anos completos", ativo: true, instituicao: "", tiposOperacao: [] },
  { id: "5", titulo: "Idade Máxima", descricao: "80 anos ao final do contrato", ativo: true, instituicao: "", tiposOperacao: [] },
  { id: "6", titulo: "Margem Mínima", descricao: "R$ 50,00 para Crédito Pessoal", ativo: true, instituicao: "", tiposOperacao: [] },
];

// Tipos de operação para roteiros e parâmetros
const TIPOS_OPERACAO_ROTEIRO = [
  { value: "CARTAO_BENEFICIO", label: "CARTÃO BENEFÍCIO", color: "bg-pink-500 hover:bg-pink-600" },
  { value: "CARTAO_CONSIGNADO", label: "CARTÃO CONSIGNADO", color: "bg-purple-500 hover:bg-purple-600" },
  { value: "CREDITO_CLT", label: "CRÉDITO CLT", color: "bg-blue-500 hover:bg-blue-600" },
  { value: "FGTS", label: "FGTS", color: "bg-orange-500 hover:bg-orange-600" },
  { value: "MARGEM_NOVA", label: "MARGEM NOVA", color: "bg-teal-500 hover:bg-teal-600" },
  { value: "PORTABILIDADE", label: "PORTABILIDADE", color: "bg-emerald-500 hover:bg-emerald-600" },
  { value: "REFINANCIAMENTO", label: "REFINANCIAMENTO", color: "bg-amber-500 hover:bg-amber-600" }
];

export default function BancoEditar() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "novo";

  const [activeTab, setActiveTab] = useState("dados");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Estado do banco
  const [banco, setBanco] = useState({
    codigo: "",
    nome: "",
    parceiro: false,
    creditoPessoal: false,
    dbt: false,
    seguro: false,
    cartao: false,
    refin: false,
    portabilidade: false,
    novaMargem: false,
    saqueFgts: false,
    emprestimoClt: false,
    observacoes: "",
    logo: "",
    acessos: [],
  });
  const [correspondentes, setCorrespondentes] = useState([]);
  const [showSenha, setShowSenha] = useState({});

  // Estados para as tabelas
  const [fatores, setFatores] = useState(fatoresIniciais);
  const [comissoes, setComissoes] = useState(comissoesIniciais);
  const [roteiros, setRoteiros] = useState(roteirosIniciais);
  
  // Estados para instituições
  const [instituicoesVinculadas, setInstituicoesVinculadas] = useState([]);
  const [todasInstituicoes, setTodasInstituicoes] = useState([]);
  const [loadingInstituicoes, setLoadingInstituicoes] = useState(false);
  const [buscarInstituicao, setBuscarInstituicao] = useState("");

  // Estados para adicionar novos itens
  const [novoFatorDialogOpen, setNovoFatorDialogOpen] = useState(false);
  const [novoFator, setNovoFator] = useState({ prazo: "", fator: "", taxa: "" });

  const [novaComissaoDialogOpen, setNovaComissaoDialogOpen] = useState(false);
  const [editandoComissao, setEditandoComissao] = useState(null); // ID da comissão sendo editada
  const [novaComissao, setNovaComissao] = useState({ 
    produto: "",
    prazo: "",
    fator: "",
    taxa: "",
    tipo: "",
    instituicao: "", // Instituição vinculada à comissão
    nomeTabela: "", // Nome da tabela de comissão
    empresa: "", 
    teleVenda: "", teleVendaModelo: "COMISSAO_EMPRESA",
    corretor: "", corretorModelo: "COMISSAO_EMPRESA",
    atendente: "", atendenteModelo: "COMISSAO_EMPRESA",
    gerAdm: "", gerAdmModelo: "COMISSAO_EMPRESA",
    gerOper: "", gerOperModelo: "COMISSAO_EMPRESA",
    // Novos campos
    valorInicial: "",
    valorFinal: "",
    bonus: "",
    diferido: "",
    dataInicioVigencia: "",
    dataFimVigencia: "",
    status: "Ativo"
  });

  const [novoRoteiroDialogOpen, setNovoRoteiroDialogOpen] = useState(false);
  const [novoRoteiro, setNovoRoteiro] = useState({ titulo: "", descricao: "", instituicao: "", tiposOperacao: [] });

  // Estados para Parâmetros de Operação
  const [parametros, setParametros] = useState([]);
  const [novoParametroDialogOpen, setNovoParametroDialogOpen] = useState(false);
  const [editandoParametro, setEditandoParametro] = useState(null);
  const [novoParametro, setNovoParametro] = useState({
    instituicao: "",
    tipoOperacao: "",
    // Campos específicos INSS
    especie: "", // Código da espécie de benefício
    lotacao: "", // Lotação/Unidade
    idadeMinima: "",
    idadeMaxima: "",
    idadeMaximaMeses: "", // Meses da idade máxima
    idadeMaximaDias: "",  // Dias da idade máxima
    parcelasPagas: "",
    valorParcelaMinima: "",
    valorParcelaMaxima: "",
    saldoDevedorMinimo: "",
    valorTroco: "",
    valorRCO: "",
    indiceSaldo1a30: "",
    indiceSaldo31a50: "",
    indiceSaldo51a100: "",
    taxaJurosMin: "",
    taxaJurosMax: "",
    observacoes: "",
    // Regras de Portabilidade
    bancosNaoPorta: [], // Bancos que NÃO aceita portabilidade
    bancosPortaRegras: [], // Bancos com parcelas mínimas específicas {banco, parcelasPagas}
    parcelasPagasDefault: "1", // Parcelas pagas para bancos não listados
    // Faixas Etárias - Prazo e Limite por Idade
    faixasEtarias: [], // Array de {idadeInicial, idadeFinal, prazoMaximo, limiteMaximo}
    idadeMaximaFimOperacao: "" // Idade máxima para fim da operação
  });

  // Estados para adicionar bancos às regras de portabilidade
  const [novoBancoNaoPorta, setNovoBancoNaoPorta] = useState("");
  const [novoBancoPorta, setNovoBancoPorta] = useState({ banco: "", parcelasPagas: "" });
  const [todosBancos, setTodosBancos] = useState([]);
  const [especiesINSS, setEspeciesINSS] = useState([]);
  
  // Estado para adicionar faixa etária
  const [novaFaixaEtaria, setNovaFaixaEtaria] = useState({ 
    idadeInicial: "", 
    idadeFinal: "", 
    idadeFinalMeses: "",
    idadeFinalDias: "",
    prazoMaximo: "", 
    limiteMaximo: "" 
  });

  // Estados para importação de comissões via Excel
  const [importarComissaoDialogOpen, setImportarComissaoDialogOpen] = useState(false);
  const [arquivoComissao, setArquivoComissao] = useState(null);
  const [importandoComissao, setImportandoComissao] = useState(false);
  const [nomeTabela, setNomeTabela] = useState("");
  const [instituicaoImport, setInstituicaoImport] = useState("");

  // Carregar banco existente
  useEffect(() => {
    carregarTodasInstituicoes();
    carregarTodosBancos();
    carregarEspeciesINSS();
    if (!isNew) {
      carregarBanco();
    }
  }, [isNew, id]);

  // Carregar todos os bancos para regras de portabilidade
  const carregarTodosBancos = async () => {
    try {
      const response = await axios.get(`${API}/bancos`);
      const bancos = response.data || [];
      setTodosBancos(bancos.map(b => ({ codigo: b.codigo, nome: b.nome })));
    } catch (error) {
      console.error("Erro ao carregar bancos:", error);
    }
  };

  // Carregar espécies de benefício do INSS
  const carregarEspeciesINSS = async () => {
    try {
      const response = await axios.get(`${API}/especies-inss`);
      const especies = response.data || [];
      setEspeciesINSS(especies);
    } catch (error) {
      console.error("Erro ao carregar espécies INSS:", error);
    }
  };

  // Carregar todas as instituições disponíveis
  const carregarTodasInstituicoes = async () => {
    try {
      const response = await axios.get(`${API}/lookup/instituicoes`);
      const instituicoes = response.data?.instituicoes || [];
      // Processar para garantir formato {codigo, nome}
      const processadas = instituicoes.map(inst => {
        if (typeof inst === 'string') {
          const parts = inst.split(' - ');
          return { codigo: parts[0]?.trim(), nome: parts[1]?.trim() || parts[0] };
        }
        return inst;
      });
      setTodasInstituicoes(processadas);
    } catch (error) {
      console.error("Erro ao carregar instituições:", error);
    }
  };

  const carregarBanco = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bancos/${id}`);
      if (response.data) {
        setBanco({
          codigo: response.data.codigo || "",
          nome: response.data.nome || "",
          parceiro: response.data.parceiro || false,
          creditoPessoal: response.data.creditoPessoal || false,
          dbt: response.data.dbt || false,
          seguro: response.data.seguro || false,
          cartao: response.data.cartao || false,
          refin: response.data.refin || false,
          portabilidade: response.data.portabilidade || false,
          novaMargem: response.data.novaMargem || false,
          saqueFgts: response.data.saqueFgts || false,
          emprestimoClt: response.data.emprestimoClt || false,
          observacoes: response.data.observacoes || "",
          logo: response.data.logo || "",
          acessos: response.data.acessos || [],
        });
        setFatores(response.data.fatores || []);
        setComissoes(response.data.comissoes || []);
        setRoteiros(response.data.roteiros || []);
        setParametros(response.data.parametros || []);
        setInstituicoesVinculadas(response.data.instituicoes || []);
      }
    } catch (error) {
      console.error("Erro ao carregar banco:", error);
      toast.error("Erro ao carregar dados do banco");
    } finally {
      setLoading(false);
    }
  };

  // Salvar banco
  useEffect(() => {
    axios.get(`${API}/correspondentes`).then(r => setCorrespondentes(r.data?.correspondentes || [])).catch(() => {});
  }, []);

  const adicionarAcesso = () => {
    if ((banco.acessos || []).length >= 3) return;
    setBanco({ ...banco, acessos: [...(banco.acessos || []), { correspondente: "", link: "", usuario: "", senha: "" }] });
  };
  const removerAcesso = (idx) => setBanco({ ...banco, acessos: banco.acessos.filter((_, i) => i !== idx) });
  const updateAcesso = (idx, campo, val) => setBanco({ ...banco, acessos: banco.acessos.map((a, i) => i === idx ? { ...a, [campo]: val } : a) });
  const abrirAcesso = (ac) => {
    if (!ac.link) return;
    const url = ac.link.startsWith("http") ? ac.link : `https://${ac.link}`;
    if (navigator.clipboard) navigator.clipboard.writeText(`Usuario: ${ac.usuario}\nSenha: ${ac.senha}`).catch(() => {});
    window.open(url, "_blank");
    toast.success("Portal aberto. Usuario e senha copiados para colar.");
  };
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Selecione uma imagem"); return; }
    if (file.size > 500 * 1024) { toast.error("Imagem muito grande (max 500KB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setBanco((b) => ({ ...b, logo: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleSalvar = async () => {
    if (!banco.nome.trim()) {
      toast.error("Informe o nome do banco");
      return;
    }

    setSaving(true);
    try {
      const dadosBanco = {
        ...banco,
        fatores: fatores,
        comissoes: comissoes,
        roteiros: roteiros,
        parametros: parametros,
        instituicoes: instituicoesVinculadas
      };

      if (isNew) {
        await axios.post(`${API}/bancos`, dadosBanco);
        toast.success("Banco cadastrado com sucesso!");
      } else {
        await axios.put(`${API}/bancos/${id}`, dadosBanco);
        toast.success("Banco atualizado com sucesso!");
      }
      navigate("/bancos");
    } catch (error) {
      console.error("Erro ao salvar banco:", error);
      toast.error("Erro ao salvar banco");
    } finally {
      setSaving(false);
    }
  };

  // Adicionar novo fator
  const adicionarFator = () => {
    if (!novoFator.prazo || !novoFator.fator || !novoFator.taxa) {
      toast.error("Preencha todos os campos");
      return;
    }
    setFatores(prev => [...prev, {
      id: String(Date.now()),
      prazo: Number(novoFator.prazo),
      fator: Number(novoFator.fator),
      taxa: Number(novoFator.taxa),
    }]);
    setNovoFator({ prazo: "", fator: "", taxa: "" });
    setNovoFatorDialogOpen(false);
    toast.success("Fator adicionado");
  };

  // Excluir fator
  const excluirFator = (fatorId) => {
    setFatores(prev => prev.filter(f => f.id !== fatorId));
    toast.success("Fator excluído");
  };

  // Adicionar ou atualizar comissão
  const salvarComissao = () => {
    if (!novaComissao.produto && !novaComissao.tipo) {
      toast.error("Informe o produto ou tipo de operação");
      return;
    }
    
    const comissaoData = {
      id: editandoComissao || String(Date.now()),
      produto: novaComissao.produto || "",
      prazo: novaComissao.prazo || "",
      fator: Number(novaComissao.fator) || null,
      taxa: Number(novaComissao.taxa) || null,
      tipo: novaComissao.tipo || "",
      instituicao: novaComissao.instituicao || "", // Instituição vinculada
      nomeTabela: novaComissao.nomeTabela || "", // Nome da tabela
      empresa: Number(novaComissao.empresa) || 0,
      teleVenda: Number(novaComissao.teleVenda) || 0,
      teleVendaModelo: novaComissao.teleVendaModelo,
      corretor: Number(novaComissao.corretor) || 0,
      corretorModelo: novaComissao.corretorModelo,
      atendente: Number(novaComissao.atendente) || 0,
      atendenteModelo: novaComissao.atendenteModelo,
      gerAdm: Number(novaComissao.gerAdm) || 0,
      gerAdmModelo: novaComissao.gerAdmModelo,
      gerOper: Number(novaComissao.gerOper) || 0,
      gerOperModelo: novaComissao.gerOperModelo,
      // Novos campos
      valorInicial: novaComissao.valorInicial ? Number(novaComissao.valorInicial) : null,
      valorFinal: novaComissao.valorFinal ? Number(novaComissao.valorFinal) : null,
      bonus: Number(novaComissao.bonus) || 0,
      diferido: Number(novaComissao.diferido) || 0,
      dataInicioVigencia: novaComissao.dataInicioVigencia || null,
      dataFimVigencia: novaComissao.dataFimVigencia || null,
      status: novaComissao.status || "Ativo"
    };
    
    if (editandoComissao) {
      // Atualizar existente
      setComissoes(prev => prev.map(c => c.id === editandoComissao ? comissaoData : c));
      toast.success("Comissão atualizada");
    } else {
      // Adicionar nova
      setComissoes(prev => [...prev, comissaoData]);
      toast.success("Comissão adicionada");
    }
    
    limparFormComissao();
    setNovaComissaoDialogOpen(false);
  };

  // Limpar formulário de comissão
  const limparFormComissao = () => {
    setNovaComissao({ 
      produto: "",
      prazo: "",
      fator: "",
      taxa: "",
      tipo: "",
      instituicao: "",
      nomeTabela: "",
      empresa: "", 
      teleVenda: "", teleVendaModelo: "COMISSAO_EMPRESA",
      corretor: "", corretorModelo: "COMISSAO_EMPRESA",
      atendente: "", atendenteModelo: "COMISSAO_EMPRESA",
      gerAdm: "", gerAdmModelo: "COMISSAO_EMPRESA",
      gerOper: "", gerOperModelo: "COMISSAO_EMPRESA",
      // Novos campos
      valorInicial: "",
      valorFinal: "",
      bonus: "",
      diferido: "",
      dataInicioVigencia: "",
      dataFimVigencia: "",
      status: "Ativo"
    });
    setEditandoComissao(null);
  };

  // Abrir modal para editar comissão
  const editarComissao = (comissao) => {
    setNovaComissao({
      produto: comissao.produto || "",
      prazo: comissao.prazo || "",
      fator: comissao.fator || "",
      taxa: comissao.taxa || "",
      tipo: comissao.tipo || "",
      instituicao: comissao.instituicao || "",
      nomeTabela: comissao.nomeTabela || "",
      empresa: comissao.empresa || "",
      teleVenda: comissao.teleVenda || "",
      teleVendaModelo: comissao.teleVendaModelo || "COMISSAO_EMPRESA",
      corretor: comissao.corretor || "",
      corretorModelo: comissao.corretorModelo || "COMISSAO_EMPRESA",
      atendente: comissao.atendente || "",
      atendenteModelo: comissao.atendenteModelo || "COMISSAO_EMPRESA",
      gerAdm: comissao.gerAdm || "",
      gerAdmModelo: comissao.gerAdmModelo || "COMISSAO_EMPRESA",
      gerOper: comissao.gerOper || "",
      gerOperModelo: comissao.gerOperModelo || "COMISSAO_EMPRESA",
      // Novos campos
      valorInicial: comissao.valorInicial || "",
      valorFinal: comissao.valorFinal || "",
      bonus: comissao.bonus || "",
      diferido: comissao.diferido || "",
      dataInicioVigencia: comissao.dataInicioVigencia || "",
      dataFimVigencia: comissao.dataFimVigencia || "",
      status: comissao.status || "Ativo"
    });
    setEditandoComissao(comissao.id);
    setNovaComissaoDialogOpen(true);
  };

  // Abrir modal para nova comissão
  const abrirNovaComissao = () => {
    limparFormComissao();
    setNovaComissaoDialogOpen(true);
  };

  // Excluir comissão
  const excluirComissao = (comissaoId) => {
    setComissoes(prev => prev.filter(c => c.id !== comissaoId));
    toast.success("Comissão excluída");
  };

  // Adicionar novo roteiro
  const adicionarRoteiro = () => {
    if (!novoRoteiro.titulo.trim() || !novoRoteiro.descricao.trim()) {
      toast.error("Preencha todos os campos");
      return;
    }
    setRoteiros(prev => [...prev, {
      id: String(Date.now()),
      titulo: novoRoteiro.titulo,
      descricao: novoRoteiro.descricao,
      instituicao: novoRoteiro.instituicao,
      tiposOperacao: novoRoteiro.tiposOperacao || [],
      ativo: true,
    }]);
    setNovoRoteiro({ titulo: "", descricao: "", instituicao: "", tiposOperacao: [] });
    setNovoRoteiroDialogOpen(false);
    toast.success("Roteiro adicionado");
  };

  // Excluir roteiro
  const excluirRoteiro = (roteiroId) => {
    setRoteiros(prev => prev.filter(r => r.id !== roteiroId));
    toast.success("Roteiro excluído");
  };

  // Toggle roteiro ativo
  const toggleRoteiroAtivo = (roteiroId) => {
    setRoteiros(prev => prev.map(r => 
      r.id === roteiroId ? { ...r, ativo: !r.ativo } : r
    ));
  };

  // Funções para Parâmetros
  const limparFormParametro = () => {
    setNovoParametro({
      instituicao: "",
      tipoOperacao: "",
      especie: "",
      lotacao: "",
      idadeMinima: "",
      idadeMaxima: "",
      idadeMaximaMeses: "",
      idadeMaximaDias: "",
      parcelasPagas: "",
      valorParcelaMinima: "",
      valorParcelaMaxima: "",
      saldoDevedorMinimo: "",
      valorTroco: "",
      valorRCO: "",
      indiceSaldo1a30: "",
      indiceSaldo31a50: "",
      indiceSaldo51a100: "",
      taxaJurosMin: "",
      taxaJurosMax: "",
      observacoes: "",
      bancosNaoPorta: [],
      bancosPortaRegras: [],
      parcelasPagasDefault: "1",
      faixasEtarias: [],
      idadeMaximaFimOperacao: ""
    });
    setEditandoParametro(null);
    setNovoBancoNaoPorta("");
    setNovoBancoPorta({ banco: "", parcelasPagas: "" });
    setNovaFaixaEtaria({ idadeInicial: "", idadeFinal: "", idadeFinalMeses: "", idadeFinalDias: "", prazoMaximo: "", limiteMaximo: "" });
  };

  // Funções para Faixas Etárias
  const adicionarFaixaEtaria = () => {
    if (!novaFaixaEtaria.idadeInicial || !novaFaixaEtaria.idadeFinal || !novaFaixaEtaria.prazoMaximo) {
      toast.error("Preencha idade inicial, idade final e prazo máximo");
      return;
    }
    setNovoParametro(prev => ({
      ...prev,
      faixasEtarias: [...prev.faixasEtarias, { ...novaFaixaEtaria, id: String(Date.now()) }]
    }));
    setNovaFaixaEtaria({ idadeInicial: "", idadeFinal: "", idadeFinalMeses: "", idadeFinalDias: "", prazoMaximo: "", limiteMaximo: "" });
  };

  const removerFaixaEtaria = (faixaId) => {
    setNovoParametro(prev => ({
      ...prev,
      faixasEtarias: prev.faixasEtarias.filter(f => f.id !== faixaId)
    }));
  };

  // Adicionar banco à lista "Não Porta"
  const adicionarBancoNaoPorta = () => {
    if (!novoBancoNaoPorta) return;
    if (novoParametro.bancosNaoPorta.includes(novoBancoNaoPorta)) {
      toast.error("Banco já adicionado");
      return;
    }
    const novosBancos = [...novoParametro.bancosNaoPorta, novoBancoNaoPorta];
    setNovoParametro(prev => ({
      ...prev,
      bancosNaoPorta: novosBancos
    }));
    setNovoBancoNaoPorta("");
    
    // Salvar automaticamente
    salvarParametroAutomatico({ ...novoParametro, bancosNaoPorta: novosBancos });
  };

  // Remover banco da lista "Não Porta"
  const removerBancoNaoPorta = (bancoNome) => {
    const novosBancos = novoParametro.bancosNaoPorta.filter(b => b !== bancoNome);
    setNovoParametro(prev => ({
      ...prev,
      bancosNaoPorta: novosBancos
    }));
    
    // Salvar automaticamente
    salvarParametroAutomatico({ ...novoParametro, bancosNaoPorta: novosBancos });
  };

  // Adicionar banco à lista "Porta com regras"
  const adicionarBancoPortaRegra = () => {
    if (!novoBancoPorta.banco || !novoBancoPorta.parcelasPagas) {
      toast.error("Selecione o banco e informe as parcelas");
      return;
    }
    if (novoParametro.bancosPortaRegras.some(b => b.banco === novoBancoPorta.banco)) {
      toast.error("Banco já adicionado");
      return;
    }
    const novasRegras = [...novoParametro.bancosPortaRegras, { ...novoBancoPorta }];
    setNovoParametro(prev => ({
      ...prev,
      bancosPortaRegras: novasRegras
    }));
    setNovoBancoPorta({ banco: "", parcelasPagas: "" });
    
    // Salvar automaticamente
    salvarParametroAutomatico({ ...novoParametro, bancosPortaRegras: novasRegras });
  };

  // Remover banco da lista "Porta com regras"
  const removerBancoPortaRegra = (bancoNome) => {
    const novasRegras = novoParametro.bancosPortaRegras.filter(b => b.banco !== bancoNome);
    setNovoParametro(prev => ({
      ...prev,
      bancosPortaRegras: novasRegras
    }));
    
    // Salvar automaticamente
    salvarParametroAutomatico({ ...novoParametro, bancosPortaRegras: novasRegras });
  };

  // Função para salvar parâmetro automaticamente quando adiciona/remove regras
  const salvarParametroAutomatico = (dadosParametro) => {
    // Só salva se tiver instituição e tipo de operação definidos
    if (!dadosParametro.instituicao || !dadosParametro.tipoOperacao) return;
    
    if (editandoParametro) {
      // Editando existente - atualiza
      setParametros(prev => prev.map(p => 
        p.id === editandoParametro ? { ...dadosParametro, id: editandoParametro } : p
      ));
      toast.success("Regra salva automaticamente", { duration: 1500 });
    } else {
      // Novo parâmetro - cria e define como editando
      const novoId = String(Date.now());
      setParametros(prev => [...prev, {
        ...dadosParametro,
        id: novoId,
        ativo: true
      }]);
      setEditandoParametro(novoId);
      toast.success("Parâmetro criado e regra adicionada", { duration: 1500 });
    }
  };

  const abrirEditarParametro = (param) => {
    setNovoParametro({ ...param });
    setEditandoParametro(param.id);
    setNovoParametroDialogOpen(true);
  };

  const duplicarParametro = (param) => {
    const { id, ...rest } = param;
    setNovoParametro({ ...rest, tipoOperacao: "", ativo: true });
    setEditandoParametro(null);
    setNovoParametroDialogOpen(true);
    toast.info("Duplicando parametro - selecione o novo Tipo de Operacao e salve");
  };

  const salvarParametro = () => {
    if (!novoParametro.instituicao || !novoParametro.tipoOperacao) {
      toast.error("Selecione a instituição e o tipo de operação");
      return;
    }

    if (editandoParametro) {
      // Editando existente
      setParametros(prev => prev.map(p => 
        p.id === editandoParametro ? { ...novoParametro, id: editandoParametro } : p
      ));
      toast.success("Parâmetro atualizado");
    } else {
      // Adicionando novo
      setParametros(prev => [...prev, {
        ...novoParametro,
        id: String(Date.now()),
        ativo: true
      }]);
      toast.success("Parâmetro adicionado");
    }
    
    limparFormParametro();
    setNovoParametroDialogOpen(false);
  };

  const excluirParametro = (paramId) => {
    setParametros(prev => prev.filter(p => p.id !== paramId));
    toast.success("Parâmetro excluído");
  };

  const toggleParametroAtivo = (paramId) => {
    setParametros(prev => prev.map(p => 
      p.id === paramId ? { ...p, ativo: !p.ativo } : p
    ));
  };

  // Importar comissões via Excel
  const importarComissoesExcel = async () => {
    if (!arquivoComissao) {
      toast.error("Selecione um arquivo Excel");
      return;
    }
    if (!nomeTabela.trim()) {
      toast.error("Informe o nome da tabela");
      return;
    }

    setImportandoComissao(true);
    try {
      const formData = new FormData();
      formData.append("file", arquivoComissao);
      formData.append("banco_codigo", banco.codigo);
      formData.append("banco_nome", banco.nome);
      formData.append("nome_tabela", nomeTabela);
      formData.append("instituicao_codigo", instituicaoImport || "");
      
      // Buscar nome da instituição se selecionada
      if (instituicaoImport) {
        const inst = todasInstituicoes.find(i => i.codigo === instituicaoImport);
        formData.append("instituicao_nome", inst?.nome || "");
      }

      const response = await axios.post(`${API}/bancos/${id}/importar-comissoes`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      // Atualizar comissões locais com os dados importados
      if (response.data?.comissoes) {
        setComissoes(response.data.comissoes);
      }
      
      toast.success(`Importação concluída! ${response.data?.total_importados || 0} itens importados.`);
      setImportarComissaoDialogOpen(false);
      setArquivoComissao(null);
      setNomeTabela("");
      setInstituicaoImport("");
    } catch (error) {
      console.error("Erro ao importar:", error);
      toast.error(error.response?.data?.detail || "Erro ao importar arquivo");
    } finally {
      setImportandoComissao(false);
    }
  };

  // Funções para instituições
  const adicionarInstituicao = (instituicaoCodigo) => {
    if (instituicoesVinculadas.includes(instituicaoCodigo)) return;
    setInstituicoesVinculadas(prev => [...prev, instituicaoCodigo]);
    const inst = todasInstituicoes.find(i => i.codigo === instituicaoCodigo);
    toast.success(`${inst?.nome || instituicaoCodigo} adicionada`);
  };

  const removerInstituicao = (instituicaoCodigo) => {
    setInstituicoesVinculadas(prev => prev.filter(i => i !== instituicaoCodigo));
    const inst = todasInstituicoes.find(i => i.codigo === instituicaoCodigo);
    toast.success(`${inst?.nome || instituicaoCodigo} removida`);
  };

  const getNomeInstituicao = (codigo) => {
    const inst = todasInstituicoes.find(i => i.codigo === codigo);
    return inst?.nome || codigo;
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/bancos")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Building2 className="h-6 w-6 text-blue-600" />
              {isNew ? "Novo Banco" : `Editar Banco: ${banco.nome}`}
            </h1>
            <p className="text-muted-foreground">Configure dados, fatores, comissões e roteiros operacionais</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate("/bancos")}>
            Cancelar
          </Button>
          <Button onClick={handleSalvar} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Salvar
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-5 w-full max-w-4xl">
          <TabsTrigger value="dados" className="gap-1">
            <Building2 className="h-4 w-4" />
            Dados Básicos
          </TabsTrigger>
          <TabsTrigger value="instituicoes" className="gap-1">
            <Landmark className="h-4 w-4" />
            Instituições
          </TabsTrigger>
          <TabsTrigger value="fatores" className="gap-1">
            <Calculator className="h-4 w-4" />
            Fatores
          </TabsTrigger>
          <TabsTrigger value="parametros" className="gap-1">
            <Settings className="h-4 w-4" />
            Parâmetros
          </TabsTrigger>
          <TabsTrigger value="roteiros" className="gap-1">
            <ClipboardList className="h-4 w-4" />
            Roteiros
          </TabsTrigger>
        </TabsList>

        {/* Tab: Dados Básicos */}
        <TabsContent value="dados">
          <Card>
            <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 border-b">
              <CardTitle className="text-blue-800 flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Dados Básicos do Banco
              </CardTitle>
              <CardDescription>Informações gerais e funcionalidades habilitadas</CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {/* Código e Nome */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="codigo">Código do Banco</Label>
                  <Input
                    id="codigo"
                    placeholder="Ex: 318"
                    value={banco.codigo}
                    onChange={(e) => setBanco({ ...banco, codigo: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome do Banco</Label>
                  <Input
                    id="nome"
                    placeholder="Ex: BMG"
                    value={banco.nome}
                    onChange={(e) => setBanco({ ...banco, nome: e.target.value })}
                  />
                </div>
              </div>

              {/* Checkboxes de funcionalidades */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Funcionalidades Habilitadas</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="parceiro"
                      checked={banco.parceiro}
                      onCheckedChange={(checked) => setBanco({ ...banco, parceiro: checked })}
                    />
                    <Label htmlFor="parceiro" className="cursor-pointer">Parceiro</Label>
                  </div>
                </div>
              </div>

              {/* Logo do Banco */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Logo do Banco</Label>
                <div className="flex items-center gap-4">
                  {banco.logo ? (
                    <img src={banco.logo} alt="logo" className="h-16 w-16 object-contain rounded border bg-white p-1" data-testid="banco-logo-preview" />
                  ) : (
                    <div className="h-16 w-16 rounded border bg-slate-50 flex items-center justify-center text-slate-300">
                      <Building2 className="h-7 w-7" />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input id="banco-logo-input" type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                    <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById("banco-logo-input").click()} data-testid="banco-logo-upload">
                      <Upload className="h-4 w-4 mr-1" /> Enviar logo
                    </Button>
                    {banco.logo && (
                      <Button type="button" variant="ghost" size="sm" className="text-red-500" onClick={() => setBanco((b) => ({ ...b, logo: "" }))} data-testid="banco-logo-remover">Remover</Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Acessos ao portal do banco */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Acessos ao Portal (ate 3)</Label>
                  {(banco.acessos || []).length < 3 && (
                    <Button type="button" variant="outline" size="sm" onClick={adicionarAcesso} data-testid="acesso-adicionar">
                      <Plus className="h-4 w-4 mr-1" /> Adicionar acesso ({(banco.acessos || []).length}/3)
                    </Button>
                  )}
                </div>
                {(banco.acessos || []).length === 0 ? (
                  <div className="p-4 bg-slate-50 rounded-lg text-center text-slate-500 text-sm">Nenhum acesso cadastrado. Clique em "Adicionar acesso".</div>
                ) : (
                  <div className="space-y-2">
                    {banco.acessos.map((ac, idx) => (
                      <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end p-3 bg-slate-50 rounded-lg border" data-testid={`acesso-row-${idx}`}>
                        <div className="md:col-span-3 space-y-1">
                          <Label className="text-xs">Correspondente</Label>
                          <Select value={ac.correspondente || ""} onValueChange={(v) => updateAcesso(idx, "correspondente", v)}>
                            <SelectTrigger className="h-9" data-testid={`acesso-corresp-${idx}`}><SelectValue placeholder="Selecione" /></SelectTrigger>
                            <SelectContent>
                              {correspondentes.map((cr) => (<SelectItem key={cr} value={cr}>{cr}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="md:col-span-3 space-y-1">
                          <Label className="text-xs">Link de acesso</Label>
                          <Input className="h-9" placeholder="https://..." value={ac.link || ""} onChange={(e) => updateAcesso(idx, "link", e.target.value)} data-testid={`acesso-link-${idx}`} />
                        </div>
                        <div className="md:col-span-2 space-y-1">
                          <Label className="text-xs">Usuario</Label>
                          <Input className="h-9" value={ac.usuario || ""} onChange={(e) => updateAcesso(idx, "usuario", e.target.value)} data-testid={`acesso-user-${idx}`} />
                        </div>
                        <div className="md:col-span-2 space-y-1">
                          <Label className="text-xs">Senha</Label>
                          <div className="relative">
                            <Input className="h-9 pr-8" type={showSenha[idx] ? "text" : "password"} value={ac.senha || ""} onChange={(e) => updateAcesso(idx, "senha", e.target.value)} data-testid={`acesso-senha-${idx}`} />
                            <button type="button" onClick={() => setShowSenha((s) => ({ ...s, [idx]: !s[idx] }))} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid={`acesso-toggle-senha-${idx}`}>
                              {showSenha[idx] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                        <div className="md:col-span-2 flex gap-1">
                          <Button type="button" size="sm" variant="outline" className="flex-1" onClick={() => abrirAcesso(ac)} disabled={!ac.link} data-testid={`acesso-abrir-${idx}`} title="Abrir portal e copiar login">
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          <Button type="button" size="sm" variant="ghost" className="text-red-500" onClick={() => removerAcesso(idx)} data-testid={`acesso-remover-${idx}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Observações */}
              <div className="space-y-2">
                <Label htmlFor="observacoes">Observações</Label>
                <Textarea
                  id="observacoes"
                  placeholder="Observações sobre este banco..."
                  value={banco.observacoes}
                  onChange={(e) => setBanco({ ...banco, observacoes: e.target.value })}
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Instituições */}
        <TabsContent value="instituicoes">
          <Card>
            <CardHeader className="bg-gradient-to-r from-cyan-50 to-cyan-100 border-b">
              <CardTitle className="text-cyan-800 flex items-center gap-2">
                <Landmark className="h-5 w-5" />
                Instituições Vinculadas
              </CardTitle>
              <CardDescription>Selecione as instituições que este banco trabalha</CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {/* Instituições Vinculadas */}
              <div>
                <Label className="text-sm font-semibold mb-3 block">
                  Instituições Selecionadas ({instituicoesVinculadas.length})
                </Label>
                {instituicoesVinculadas.length === 0 ? (
                  <div className="p-6 bg-slate-50 rounded-lg text-center text-slate-500">
                    <Landmark className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                    <p className="font-medium">Nenhuma instituição vinculada</p>
                    <p className="text-sm">Selecione as instituições abaixo para vincular ao banco</p>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 p-4 bg-cyan-50 rounded-lg border border-cyan-200 min-h-[80px]">
                    {instituicoesVinculadas.map(instCodigo => (
                      <Badge 
                        key={instCodigo} 
                        className="bg-cyan-100 text-cyan-800 border-cyan-300 flex items-center gap-1 pr-1 py-1.5"
                      >
                        <span className="font-mono text-xs text-cyan-600 mr-1">{instCodigo}</span>
                        {getNomeInstituicao(instCodigo)}
                        <button
                          onClick={() => removerInstituicao(instCodigo)}
                          className="ml-1 hover:bg-cyan-200 rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Adicionar Instituição */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">
                  Adicionar Instituição
                </Label>
                {/* Campo de busca */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Buscar instituição por código ou nome..."
                    value={buscarInstituicao}
                    onChange={(e) => setBuscarInstituicao(e.target.value)}
                    className="pl-10 h-9"
                  />
                  {buscarInstituicao && (
                    <button 
                      onClick={() => setBuscarInstituicao("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <ScrollArea className="h-[300px] border rounded-lg">
                  <div className="p-2 space-y-1">
                    {todasInstituicoes
                      .filter(inst => !instituicoesVinculadas.includes(inst.codigo))
                      .filter(inst => {
                        if (!buscarInstituicao) return true;
                        const termo = buscarInstituicao.toLowerCase();
                        return inst.codigo.toLowerCase().includes(termo) || 
                               inst.nome.toLowerCase().includes(termo);
                      })
                      .map(inst => (
                        <div 
                          key={inst.codigo}
                          className="flex items-center justify-between p-3 hover:bg-cyan-50 rounded-lg cursor-pointer group border border-transparent hover:border-cyan-200 transition-colors"
                          onClick={() => adicionarInstituicao(inst.codigo)}
                        >
                          <span className="text-sm">
                            <span className="font-mono text-slate-500 mr-2 bg-slate-100 px-1.5 py-0.5 rounded">{inst.codigo}</span>
                            {inst.nome}
                          </span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 px-3 opacity-0 group-hover:opacity-100 transition-opacity bg-cyan-100 hover:bg-cyan-200 text-cyan-700"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Adicionar
                          </Button>
                        </div>
                      ))}
                    {todasInstituicoes
                      .filter(inst => !instituicoesVinculadas.includes(inst.codigo))
                      .filter(inst => {
                        if (!buscarInstituicao) return true;
                        const termo = buscarInstituicao.toLowerCase();
                        return inst.codigo.toLowerCase().includes(termo) || 
                               inst.nome.toLowerCase().includes(termo);
                      }).length === 0 && (
                      <div className="text-center text-sm text-slate-500 py-8">
                        {buscarInstituicao ? (
                          <>
                            <Search className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                            Nenhuma instituição encontrada para "{buscarInstituicao}"
                          </>
                        ) : (
                          <>
                            <Check className="h-8 w-8 mx-auto mb-2 text-green-500" />
                            Todas as instituições já foram vinculadas
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Tabela de Fatores */}
        <TabsContent value="fatores">
          <Card>
            <CardHeader className="bg-gradient-to-r from-purple-50 to-purple-100 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-purple-800 flex items-center gap-2">
                    <Calculator className="h-5 w-5" />
                    Tabela de Fatores
                  </CardTitle>
                  <CardDescription>Configure os fatores e taxas por prazo</CardDescription>
                </div>
                <Button 
                  className="gap-2 bg-purple-600 hover:bg-purple-700"
                  onClick={() => setNovoFatorDialogOpen(true)}
                >
                  <Plus className="h-4 w-4" />
                  Novo Fator
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow className="bg-purple-100">
                    <TableHead className="font-bold">Prazo (meses)</TableHead>
                    <TableHead className="text-center font-bold">Fator</TableHead>
                    <TableHead className="text-center font-bold">Taxa Mensal %</TableHead>
                    <TableHead className="text-center font-bold">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fatores.map((fator) => (
                    <TableRow key={fator.id} className="hover:bg-purple-50">
                      <TableCell className="font-semibold">{fator.prazo}x</TableCell>
                      <TableCell className="text-center font-mono">{fator.fator.toFixed(4)}</TableCell>
                      <TableCell className="text-center text-purple-600 font-semibold">{fator.taxa.toFixed(2)}%</TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-600 hover:bg-red-50"
                          onClick={() => excluirFator(fator.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Roteiros Operacionais */}
        <TabsContent value="roteiros">
          <Card>
            <CardHeader className="bg-gradient-to-r from-slate-100 to-slate-200 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-slate-800 flex items-center gap-2">
                    <ClipboardList className="h-5 w-5" />
                    Roteiros Operacionais
                  </CardTitle>
                  <CardDescription>Configure as regras e instruções de operação para este banco</CardDescription>
                </div>
                <div className="flex gap-2">
                  <ImportarRoteiroDialog bancoId={id} onSaved={() => {}} />
                  <Button 
                    className="gap-2 bg-slate-700 hover:bg-slate-800"
                    onClick={() => setNovoRoteiroDialogOpen(true)}
                  >
                    <Plus className="h-4 w-4" />
                    Novo Roteiro
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-3">
                {roteiros.map((roteiro) => (
                  <div 
                    key={roteiro.id} 
                    className={cn(
                      "p-4 rounded-lg border",
                      roteiro.ativo ? "bg-white" : "bg-slate-50 opacity-60"
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {/* Instituição Badge */}
                          {roteiro.instituicao && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                              {roteiro.instituicao}
                            </Badge>
                          )}
                          <h4 className="font-semibold text-slate-800">{roteiro.titulo}</h4>
                          {roteiro.ativo ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              Ativo
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-slate-100 text-slate-500">
                              Inativo
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mb-2">{roteiro.descricao}</p>
                        {/* Tipos de Operação Badges */}
                        {roteiro.tiposOperacao && roteiro.tiposOperacao.length > 0 && (
                          <div className="flex gap-1 flex-wrap">
                            {roteiro.tiposOperacao.map((tipo) => {
                              const tipoConfig = TIPOS_OPERACAO_ROTEIRO.find(t => t.value === tipo);
                              return (
                                <Badge 
                                  key={tipo} 
                                  className={cn(
                                    "text-xs",
                                    tipoConfig?.color || "bg-gray-500"
                                  )}
                                >
                                  {tipoConfig?.label || tipo}
                                </Badge>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => toggleRoteiroAtivo(roteiro.id)}
                          title={roteiro.ativo ? "Desativar" : "Ativar"}
                        >
                          {roteiro.ativo ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <X className="h-4 w-4 text-slate-400" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-600 hover:bg-red-50"
                          onClick={() => excluirRoteiro(roteiro.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Parâmetros de Operação */}
        <TabsContent value="parametros">
          <Card>
            <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-100 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-slate-800 flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Parâmetros de Operação
                  </CardTitle>
                  <CardDescription>Configure os parâmetros por instituição e tipo de operação (Portabilidade, Refin, etc.)</CardDescription>
                </div>
                <Button 
                  className="gap-2 bg-amber-600 hover:bg-amber-700"
                  onClick={() => {
                    limparFormParametro();
                    setNovoParametroDialogOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4" />
                  Novo Parâmetro
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {parametros.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Settings className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>Nenhum parâmetro cadastrado</p>
                  <p className="text-sm">Clique em "Novo Parâmetro" para adicionar</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {parametros.map((param) => {
                    const tipoConfig = TIPOS_OPERACAO_ROTEIRO.find(t => t.value === param.tipoOperacao);
                    return (
                      <div 
                        key={param.id} 
                        className={cn(
                          "p-4 rounded-lg border-2",
                          param.ativo ? "bg-white border-amber-200" : "bg-slate-50 opacity-60 border-slate-200"
                        )}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                              {param.instituicao || "Todas"}
                            </Badge>
                            <Badge className={cn("text-xs", tipoConfig?.color || "bg-gray-500")}>
                              {tipoConfig?.label || param.tipoOperacao}
                            </Badge>
                            {/* Espécie INSS */}
                            {param.especie && param.especie !== "TODAS" && (
                              <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                                Esp: {param.especie}
                              </Badge>
                            )}
                            {/* Lotação */}
                            {param.lotacao && (
                              <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200">
                                Lot: {param.lotacao}
                              </Badge>
                            )}
                            {param.ativo ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                Ativo
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-slate-100 text-slate-500">
                                Inativo
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => abrirEditarParametro(param)}
                              title="Editar"
                            >
                              <Edit className="h-4 w-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => duplicarParametro(param)}
                              title="Duplicar"
                              data-testid={`duplicar-parametro-${param.id}`}
                            >
                              <Copy className="h-4 w-4 text-amber-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => toggleParametroAtivo(param.id)}
                              title={param.ativo ? "Desativar" : "Ativar"}
                            >
                              {param.ativo ? (
                                <Check className="h-4 w-4 text-green-600" />
                              ) : (
                                <X className="h-4 w-4 text-slate-400" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-600 hover:bg-red-50"
                              onClick={() => excluirParametro(param.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Grid de Parâmetros */}
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 text-sm">
                          {param.idadeMinima && (
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-muted-foreground text-xs">Idade Mín.</span>
                              <p className="font-medium">{param.idadeMinima} anos</p>
                            </div>
                          )}
                          {param.idadeMaxima && (
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-muted-foreground text-xs">Idade Máx.</span>
                              <p className="font-medium">
                                {param.idadeMaxima} Anos
                                {param.idadeMaximaMeses ? ` e ${param.idadeMaximaMeses} Meses` : ''}
                                {param.idadeMaximaDias ? ` e ${param.idadeMaximaDias} Dias` : ''}
                              </p>
                            </div>
                          )}
                          {param.parcelasPagas && (
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-muted-foreground text-xs">Parcelas Pagas</span>
                              <p className="font-medium">{param.parcelasPagas}</p>
                            </div>
                          )}
                          {param.valorParcelaMinima && (
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-muted-foreground text-xs">Parcela Mín.</span>
                              <p className="font-medium">R$ {param.valorParcelaMinima}</p>
                            </div>
                          )}
                          {param.valorParcelaMaxima && (
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-muted-foreground text-xs">Parcela Máx.</span>
                              <p className="font-medium">R$ {param.valorParcelaMaxima}</p>
                            </div>
                          )}
                          {param.saldoDevedorMinimo && (
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-muted-foreground text-xs">Saldo Dev. Mín.</span>
                              <p className="font-medium">R$ {param.saldoDevedorMinimo}</p>
                            </div>
                          )}
                          {param.valorTroco && (
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-muted-foreground text-xs">Valor Troco</span>
                              <p className="font-medium">R$ {param.valorTroco}</p>
                            </div>
                          )}
                          {param.valorRCO && (
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-muted-foreground text-xs">Valor Contrato Mín.</span>
                              <p className="font-medium">R$ {param.valorRCO}</p>
                            </div>
                          )}
                          {param.taxaJurosMin && (
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-muted-foreground text-xs">Taxa Juros Mín.</span>
                              <p className="font-medium">{param.taxaJurosMin}%</p>
                            </div>
                          )}
                          {param.taxaJurosMax && (
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-muted-foreground text-xs">Taxa Juros Máx.</span>
                              <p className="font-medium">{param.taxaJurosMax}%</p>
                            </div>
                          )}
                          {param.indiceSaldo1a30 && (
                            <div className="bg-blue-50 p-2 rounded">
                              <span className="text-muted-foreground text-xs">Índice 1-30 parc.</span>
                              <p className="font-medium text-blue-700">{param.indiceSaldo1a30}%</p>
                            </div>
                          )}
                          {param.indiceSaldo31a50 && (
                            <div className="bg-blue-50 p-2 rounded">
                              <span className="text-muted-foreground text-xs">Índice 31-50 parc.</span>
                              <p className="font-medium text-blue-700">{param.indiceSaldo31a50}%</p>
                            </div>
                          )}
                          {param.indiceSaldo51a100 && (
                            <div className="bg-blue-50 p-2 rounded">
                              <span className="text-muted-foreground text-xs">Índice 51-100 parc.</span>
                              <p className="font-medium text-blue-700">{param.indiceSaldo51a100}%</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Faixas Etárias */}
                        {param.faixasEtarias && param.faixasEtarias.length > 0 && (
                          <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                            <span className="text-xs font-medium text-purple-700 mb-2 block">Prazo e Limite por Faixa Etária:</span>
                            <div className="bg-white rounded border overflow-hidden">
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-purple-100">
                                    <TableHead className="text-xs py-1">Idade</TableHead>
                                    <TableHead className="text-xs py-1 text-center">Prazo Máx.</TableHead>
                                    <TableHead className="text-xs py-1 text-right">Limite CPF</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {param.faixasEtarias.map((faixa, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell className="text-xs py-1">
                                        {faixa.idadeInicial} a {faixa.idadeFinal}
                                        {faixa.idadeFinalMeses ? `a${faixa.idadeFinalMeses}m` : ''}
                                        {faixa.idadeFinalDias ? `${faixa.idadeFinalDias}d` : ''}
                                      </TableCell>
                                      <TableCell className="text-xs py-1 text-center font-medium">
                                        {faixa.prazoMaximo}m
                                      </TableCell>
                                      <TableCell className="text-xs py-1 text-right font-medium text-emerald-600">
                                        {faixa.limiteMaximo ? `R$ ${parseFloat(faixa.limiteMaximo).toLocaleString('pt-BR')}` : '-'}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                            {param.idadeMaximaFimOperacao && (
                              <p className="text-xs text-purple-600 mt-1">
                                * Idade máxima para fim da operação: {param.idadeMaximaFimOperacao} anos
                              </p>
                            )}
                          </div>
                        )}
                        
                        {/* Regras de Portabilidade */}
                        {param.tipoOperacao === "PORTABILIDADE" && (
                          <div className="mt-3 space-y-2">
                            {/* Bancos que NÃO PORTA */}
                            {param.bancosNaoPorta && param.bancosNaoPorta.length > 0 && (
                              <div className="p-2 bg-red-50 rounded border border-red-200">
                                <span className="text-xs font-medium text-red-700">NÃO PORTA de:</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {param.bancosNaoPorta.map((b) => (
                                    <Badge key={b} variant="destructive" className="text-xs">
                                      {b}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Bancos que PORTA com regras */}
                            {param.bancosPortaRegras && param.bancosPortaRegras.length > 0 && (
                              <div className="p-2 bg-emerald-50 rounded border border-emerald-200">
                                <span className="text-xs font-medium text-emerald-700">PORTA com parcelas mín.:</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {param.bancosPortaRegras.map((r) => (
                                    <Badge key={r.banco} className="text-xs bg-emerald-600">
                                      {r.banco}: {r.parcelasPagas} parc.
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Default */}
                            {param.parcelasPagasDefault && (
                              <div className="text-xs text-muted-foreground">
                                Demais bancos: <strong>{param.parcelasPagasDefault} parcela(s) paga(s)</strong>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {param.observacoes && (
                          <p className="text-sm text-muted-foreground mt-2 italic">{param.observacoes}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog: Novo Fator */}
      <Dialog open={novoFatorDialogOpen} onOpenChange={setNovoFatorDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Novo Fator</DialogTitle>
            <DialogDescription>Configure o prazo, fator e taxa mensal</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Prazo (meses)</Label>
              <Input
                type="number"
                placeholder="Ex: 60"
                value={novoFator.prazo}
                onChange={(e) => setNovoFator({ ...novoFator, prazo: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Fator</Label>
              <Input
                type="number"
                step="0.0001"
                placeholder="Ex: 0.0262"
                value={novoFator.fator}
                onChange={(e) => setNovoFator({ ...novoFator, fator: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Taxa Mensal (%)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="Ex: 1.70"
                value={novoFator.taxa}
                onChange={(e) => setNovoFator({ ...novoFator, taxa: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovoFatorDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={adicionarFator}>
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Nova/Editar Comissão */}
      <Dialog open={novaComissaoDialogOpen} onOpenChange={(open) => {
        if (!open) limparFormComissao();
        setNovaComissaoDialogOpen(open);
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editandoComissao ? "Editar Comissão" : "Adicionar Nova Comissão"}</DialogTitle>
            <DialogDescription>Configure os dados da comissão e modelos de cálculo</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Vinculação: Instituição e Nome da Tabela */}
            <div className="border rounded-lg p-4 space-y-4 bg-teal-50">
              <Label className="font-semibold text-teal-800">Vinculação da Comissão</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome da Tabela</Label>
                  <Input
                    placeholder="Ex: Tabela INSS Empréstimo 2025"
                    value={novaComissao.nomeTabela}
                    onChange={(e) => setNovaComissao({ ...novaComissao, nomeTabela: e.target.value })}
                  />
                  <p className="text-xs text-teal-600">Agrupa regras de comissão relacionadas</p>
                </div>
                <div className="space-y-2">
                  <Label>Instituição</Label>
                  <Select 
                    value={novaComissao.instituicao || undefined} 
                    onValueChange={(value) => setNovaComissao({ ...novaComissao, instituicao: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a instituição" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Todas (Geral)</SelectItem>
                      {instituicoesVinculadas.map(instCodigo => {
                        const inst = todasInstituicoes.find(i => i.codigo === instCodigo);
                        return (
                          <SelectItem key={instCodigo} value={instCodigo}>
                            {inst ? `${inst.codigo} - ${inst.nome}` : instCodigo}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-teal-600">Instituição que esta comissão se aplica</p>
                </div>
              </div>
            </div>

            {/* Dados do Produto */}
            <div className="border rounded-lg p-4 space-y-4 bg-slate-50">
              <Label className="font-semibold">Dados do Produto</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label>Produto / Nome *</Label>
                  <Input
                    placeholder="Ex: INSS - EMPRÉSTIMO NOVO"
                    value={novaComissao.produto}
                    onChange={(e) => setNovaComissao({ ...novaComissao, produto: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Prazo</Label>
                  <Input
                    placeholder="Ex: 96x"
                    value={novaComissao.prazo}
                    onChange={(e) => setNovaComissao({ ...novaComissao, prazo: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fator</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    placeholder="Ex: 0.0262"
                    value={novaComissao.fator}
                    onChange={(e) => setNovaComissao({ ...novaComissao, fator: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Taxa Mensal %</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Ex: 1.8"
                    value={novaComissao.taxa}
                    onChange={(e) => setNovaComissao({ ...novaComissao, taxa: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tipo de Operação</Label>
                  <Select value={novaComissao.tipo || undefined} onValueChange={(value) => setNovaComissao({ ...novaComissao, tipo: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS_OPERACAO.map(tipo => (
                        <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Comissão Empresa */}
            <div className="border rounded-lg p-4 space-y-4 bg-blue-50">
              <div className="flex items-center justify-between">
                <Label className="font-semibold text-blue-700">Comissão Empresa %</Label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="0.0"
                  className="w-32"
                  value={novaComissao.empresa}
                  onChange={(e) => setNovaComissao({ ...novaComissao, empresa: e.target.value })}
                />
              </div>
            </div>
            
            {/* Grid de comissões com modelo */}
            <div className="border rounded-lg p-4 space-y-4 bg-slate-50">
              <Label className="font-semibold">Comissões por Função</Label>
              
              {/* Tele-Venda */}
              <div className="grid grid-cols-3 gap-3 items-center">
                <Label className="text-green-700">Tele-Venda</Label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="%"
                  value={novaComissao.teleVenda}
                  onChange={(e) => setNovaComissao({ ...novaComissao, teleVenda: e.target.value })}
                />
                <Select value={novaComissao.teleVendaModelo} onValueChange={(v) => setNovaComissao({ ...novaComissao, teleVendaModelo: v })}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODELOS_CALCULO.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Corretor */}
              <div className="grid grid-cols-3 gap-3 items-center">
                <Label className="text-yellow-700">Corretor</Label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="%"
                  value={novaComissao.corretor}
                  onChange={(e) => setNovaComissao({ ...novaComissao, corretor: e.target.value })}
                />
                <Select value={novaComissao.corretorModelo} onValueChange={(v) => setNovaComissao({ ...novaComissao, corretorModelo: v })}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODELOS_CALCULO.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Atendente */}
              <div className="grid grid-cols-3 gap-3 items-center">
                <Label className="text-orange-700">Atendente</Label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="%"
                  value={novaComissao.atendente}
                  onChange={(e) => setNovaComissao({ ...novaComissao, atendente: e.target.value })}
                />
                <Select value={novaComissao.atendenteModelo} onValueChange={(v) => setNovaComissao({ ...novaComissao, atendenteModelo: v })}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODELOS_CALCULO.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Ger. Adm */}
              <div className="grid grid-cols-3 gap-3 items-center">
                <Label className="text-purple-700">Ger. Adm.</Label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="%"
                  value={novaComissao.gerAdm}
                  onChange={(e) => setNovaComissao({ ...novaComissao, gerAdm: e.target.value })}
                />
                <Select value={novaComissao.gerAdmModelo} onValueChange={(v) => setNovaComissao({ ...novaComissao, gerAdmModelo: v })}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODELOS_CALCULO.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Ger. Oper */}
              <div className="grid grid-cols-3 gap-3 items-center">
                <Label className="text-pink-700">Ger. Oper.</Label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="%"
                  value={novaComissao.gerOper}
                  onChange={(e) => setNovaComissao({ ...novaComissao, gerOper: e.target.value })}
                />
                <Select value={novaComissao.gerOperModelo} onValueChange={(v) => setNovaComissao({ ...novaComissao, gerOperModelo: v })}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODELOS_CALCULO.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Novos Campos: Faixa de Valores */}
            <div className="border rounded-lg p-4 space-y-4 bg-slate-100">
              <Label className="font-semibold">Faixa de Valores</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Valor Inicial (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Ex: 0.01"
                    value={novaComissao.valorInicial}
                    onChange={(e) => setNovaComissao({ ...novaComissao, valorInicial: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Valor Final (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Ex: 500.00"
                    value={novaComissao.valorFinal}
                    onChange={(e) => setNovaComissao({ ...novaComissao, valorFinal: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Novos Campos: Bônus e Diferido */}
            <div className="border rounded-lg p-4 space-y-4 bg-amber-50">
              <Label className="font-semibold text-amber-700">Bônus e Diferido</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bônus (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="Ex: 0.5"
                    value={novaComissao.bonus}
                    onChange={(e) => setNovaComissao({ ...novaComissao, bonus: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Diferido (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="Ex: 0.5"
                    value={novaComissao.diferido}
                    onChange={(e) => setNovaComissao({ ...novaComissao, diferido: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Novos Campos: Vigência e Status */}
            <div className="border rounded-lg p-4 space-y-4 bg-indigo-50">
              <Label className="font-semibold text-indigo-700">Vigência e Status</Label>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Data Início Vigência</Label>
                  <Input
                    type="date"
                    value={novaComissao.dataInicioVigencia}
                    onChange={(e) => setNovaComissao({ ...novaComissao, dataInicioVigencia: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data Fim Vigência</Label>
                  <Input
                    type="date"
                    value={novaComissao.dataFimVigencia}
                    onChange={(e) => setNovaComissao({ ...novaComissao, dataFimVigencia: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={novaComissao.status || "Ativo"} onValueChange={(v) => setNovaComissao({ ...novaComissao, status: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Ativo">Ativo</SelectItem>
                      <SelectItem value="Inativo">Inativo</SelectItem>
                      <SelectItem value="Expirado">Expirado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
              <strong>Modelos de Cálculo:</strong><br/>
              • <strong>V.Contrato</strong>: Valor do Contrato × % = Comissão<br/>
              • <strong>Com.Emp</strong>: Comissão da Empresa × % = Comissão
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              limparFormComissao();
              setNovaComissaoDialogOpen(false);
            }}>
              Cancelar
            </Button>
            <Button onClick={salvarComissao} className="bg-emerald-600 hover:bg-emerald-700">
              {editandoComissao ? "Salvar Alterações" : "Adicionar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Novo Roteiro */}
      <Dialog open={novoRoteiroDialogOpen} onOpenChange={setNovoRoteiroDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Adicionar Novo Roteiro</DialogTitle>
            <DialogDescription>Configure uma regra ou instrução operacional</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Instituição */}
            <div className="space-y-2">
              <Label>Instituição</Label>
              <Select 
                value={novoRoteiro.instituicao || "TODAS"} 
                onValueChange={(value) => setNovoRoteiro({ ...novoRoteiro, instituicao: value === "TODAS" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a instituição" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TODAS">Todas as Instituições</SelectItem>
                  {instituicoesVinculadas.map((instCodigo) => {
                    // Buscar o nome da instituição nas instituições carregadas
                    const codigo = typeof instCodigo === 'string' ? instCodigo : instCodigo.codigo;
                    const instCompleta = todasInstituicoes.find(i => i.codigo === codigo);
                    const displayValue = instCompleta 
                      ? `${instCompleta.codigo} - ${instCompleta.nome}`
                      : codigo;
                    return (
                      <SelectItem key={codigo} value={displayValue}>
                        {displayValue}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Título */}
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input
                placeholder="Ex: Documentação Exigida"
                value={novoRoteiro.titulo}
                onChange={(e) => setNovoRoteiro({ ...novoRoteiro, titulo: e.target.value })}
              />
            </div>
            
            {/* Descrição */}
            <div className="space-y-2">
              <Label>Descrição *</Label>
              <Textarea
                placeholder="Descreva a regra ou instrução..."
                value={novoRoteiro.descricao}
                onChange={(e) => setNovoRoteiro({ ...novoRoteiro, descricao: e.target.value })}
                rows={3}
              />
            </div>

            {/* Tipos de Operação */}
            <div className="space-y-2">
              <Label>Tipo de Operação</Label>
              <div className="flex gap-2 flex-wrap">
                {TIPOS_OPERACAO_ROTEIRO.map((tipo) => {
                  const isSelected = novoRoteiro.tiposOperacao?.includes(tipo.value);
                  return (
                    <Button
                      key={tipo.value}
                      type="button"
                      variant={isSelected ? "default" : "outline"}
                      size="sm"
                      className={cn(
                        isSelected && tipo.color
                      )}
                      onClick={() => {
                        const current = novoRoteiro.tiposOperacao || [];
                        if (isSelected) {
                          setNovoRoteiro({ 
                            ...novoRoteiro, 
                            tiposOperacao: current.filter(t => t !== tipo.value) 
                          });
                        } else {
                          setNovoRoteiro({ 
                            ...novoRoteiro, 
                            tiposOperacao: [...current, tipo.value] 
                          });
                        }
                      }}
                    >
                      {tipo.label}
                    </Button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">Selecione os tipos de operação aplicáveis a esta regra</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovoRoteiroDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={adicionarRoteiro}>
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Novo/Editar Parâmetro */}
      <Dialog open={novoParametroDialogOpen} onOpenChange={(open) => {
        if (!open) limparFormParametro();
        setNovoParametroDialogOpen(open);
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-amber-600" />
              {editandoParametro ? "Editar Parâmetro" : "Novo Parâmetro"}
            </DialogTitle>
            <DialogDescription>Configure os parâmetros de operação por instituição</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Instituição e Tipo de Operação */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Instituição *</Label>
                <Select 
                  value={novoParametro.instituicao || "TODAS"} 
                  onValueChange={(value) => setNovoParametro({ ...novoParametro, instituicao: value === "TODAS" ? "" : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a instituição" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TODAS">Todas as Instituições</SelectItem>
                    {instituicoesVinculadas.map((instCodigo) => {
                      const codigo = typeof instCodigo === 'string' ? instCodigo : instCodigo.codigo;
                      const instCompleta = todasInstituicoes.find(i => i.codigo === codigo);
                      const displayValue = instCompleta 
                        ? `${instCompleta.codigo} - ${instCompleta.nome}`
                        : codigo;
                      return (
                        <SelectItem key={codigo} value={displayValue}>
                          {displayValue}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Tipo de Operação *</Label>
                <Select 
                  value={novoParametro.tipoOperacao} 
                  onValueChange={(value) => setNovoParametro({ ...novoParametro, tipoOperacao: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_OPERACAO_ROTEIRO.map((tipo) => (
                      <SelectItem key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Campos específicos INSS - Espécie e Lotação */}
            {novoParametro.instituicao && novoParametro.instituicao.includes("037") && (
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <h4 className="font-semibold text-orange-800 mb-3 flex items-center gap-2">
                  <Landmark className="h-4 w-4" />
                  Especificações INSS
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Espécie de Benefício</Label>
                    <Select 
                      value={novoParametro.especie} 
                      onValueChange={(value) => setNovoParametro({ ...novoParametro, especie: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a espécie (opcional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TODAS">Todas as Espécies</SelectItem>
                        {especiesINSS.map((esp) => (
                          <SelectItem key={esp.codigo} value={`${esp.codigo} - ${esp.nome}`}>
                            {esp.codigo} - {esp.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Ex: 04 - Aposentadoria por invalidez com idade mín. específica
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Lotação</Label>
                    <Input
                      placeholder="Ex: GERÊNCIA EXECUTIVA"
                      value={novoParametro.lotacao}
                      onChange={(e) => setNovoParametro({ ...novoParametro, lotacao: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Unidade/Lotação específica (opcional)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Idade */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Idade Mínima</Label>
                <Input
                  type="number"
                  placeholder="Ex: 21"
                  value={novoParametro.idadeMinima}
                  onChange={(e) => setNovoParametro({ ...novoParametro, idadeMinima: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Idade Máxima</Label>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Input
                      type="number"
                      placeholder="Anos"
                      value={novoParametro.idadeMaxima}
                      onChange={(e) => setNovoParametro({ ...novoParametro, idadeMaxima: e.target.value })}
                    />
                    <span className="text-xs text-slate-500 mt-1">Anos</span>
                  </div>
                  <div>
                    <Input
                      type="number"
                      placeholder="Meses"
                      min="0"
                      max="11"
                      value={novoParametro.idadeMaximaMeses}
                      onChange={(e) => setNovoParametro({ ...novoParametro, idadeMaximaMeses: e.target.value })}
                    />
                    <span className="text-xs text-slate-500 mt-1">Meses</span>
                  </div>
                  <div>
                    <Input
                      type="number"
                      placeholder="Dias"
                      min="0"
                      max="30"
                      value={novoParametro.idadeMaximaDias}
                      onChange={(e) => setNovoParametro({ ...novoParametro, idadeMaximaDias: e.target.value })}
                    />
                    <span className="text-xs text-slate-500 mt-1">Dias</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Faixas Etárias - Prazo e Limite por Idade */}
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                Prazo e Limite por Faixa Etária
              </h4>
              
              {/* Formulário para adicionar faixa - Layout alinhado */}
              <div className="flex items-start gap-3 mb-3">
                {/* Idade Inicial */}
                <div className="space-y-1">
                  <Label className="text-xs">Idade Inicial</Label>
                  <div>
                    <Input
                      type="number"
                      placeholder="18"
                      className="text-center w-[70px]"
                      value={novaFaixaEtaria.idadeInicial}
                      onChange={(e) => setNovaFaixaEtaria(prev => ({ ...prev, idadeInicial: e.target.value }))}
                    />
                    <span className="text-[10px] text-slate-500 block text-center">Anos</span>
                  </div>
                </div>

                {/* Idade Final (Anos, Meses, Dias) */}
                <div className="space-y-1">
                  <Label className="text-xs">Idade Final</Label>
                  <div className="flex gap-2">
                    <div>
                      <Input
                        type="number"
                        placeholder="80"
                        className="text-center w-[70px]"
                        value={novaFaixaEtaria.idadeFinal}
                        onChange={(e) => setNovaFaixaEtaria(prev => ({ ...prev, idadeFinal: e.target.value }))}
                      />
                      <span className="text-[10px] text-slate-500 block text-center">Anos</span>
                    </div>
                    <div>
                      <Input
                        type="number"
                        placeholder="11"
                        min="0"
                        max="11"
                        className="text-center w-[60px]"
                        value={novaFaixaEtaria.idadeFinalMeses}
                        onChange={(e) => setNovaFaixaEtaria(prev => ({ ...prev, idadeFinalMeses: e.target.value }))}
                      />
                      <span className="text-[10px] text-slate-500 block text-center">Meses</span>
                    </div>
                    <div>
                      <Input
                        type="number"
                        placeholder="29"
                        min="0"
                        max="30"
                        className="text-center w-[60px]"
                        value={novaFaixaEtaria.idadeFinalDias}
                        onChange={(e) => setNovaFaixaEtaria(prev => ({ ...prev, idadeFinalDias: e.target.value }))}
                      />
                      <span className="text-[10px] text-slate-500 block text-center">Dias</span>
                    </div>
                  </div>
                </div>

                {/* Prazo Máximo */}
                <div className="space-y-1">
                  <Label className="text-xs">Prazo Máx.</Label>
                  <div>
                    <Input
                      type="number"
                      placeholder="84"
                      className="text-center w-[70px]"
                      value={novaFaixaEtaria.prazoMaximo}
                      onChange={(e) => setNovaFaixaEtaria(prev => ({ ...prev, prazoMaximo: e.target.value }))}
                    />
                    <span className="text-[10px] text-slate-500 block text-center">Meses</span>
                  </div>
                </div>

                {/* Limite Máximo */}
                <div className="space-y-1">
                  <Label className="text-xs">Limite Máx. (R$)</Label>
                  <div>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="150000"
                      className="text-center w-[100px]"
                      value={novaFaixaEtaria.limiteMaximo}
                      onChange={(e) => setNovaFaixaEtaria(prev => ({ ...prev, limiteMaximo: e.target.value }))}
                    />
                    <span className="text-[10px] text-slate-500 block text-center">Valor</span>
                  </div>
                </div>

                {/* Botão Adicionar - alinhado com os inputs */}
                <div className="space-y-1">
                  <Label className="text-xs invisible">Ação</Label>
                  <div>
                    <Button 
                      type="button" 
                      size="sm" 
                      onClick={adicionarFaixaEtaria}
                      className="bg-purple-600 hover:bg-purple-700 h-9 w-9"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    <span className="text-[10px] text-transparent block text-center">-</span>
                  </div>
                </div>
              </div>
              
              {/* Tabela de faixas cadastradas */}
              {novoParametro.faixasEtarias && novoParametro.faixasEtarias.length > 0 && (
                <div className="bg-white rounded border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-purple-100">
                        <TableHead className="text-xs py-2">Idade na Contratação</TableHead>
                        <TableHead className="text-xs py-2 text-center">Prazo Máximo</TableHead>
                        <TableHead className="text-xs py-2 text-right">Limite por CPF</TableHead>
                        <TableHead className="text-xs py-2 w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {novoParametro.faixasEtarias.map((faixa) => (
                        <TableRow key={faixa.id}>
                          <TableCell className="text-sm py-2">
                            {faixa.idadeInicial} até {faixa.idadeFinal} Anos
                            {faixa.idadeFinalMeses ? ` e ${faixa.idadeFinalMeses} Meses` : ''}
                            {faixa.idadeFinalDias ? ` e ${faixa.idadeFinalDias} Dias` : ''}
                          </TableCell>
                          <TableCell className="text-sm py-2 text-center font-medium">
                            {faixa.prazoMaximo} meses
                          </TableCell>
                          <TableCell className="text-sm py-2 text-right font-medium text-emerald-600">
                            {faixa.limiteMaximo ? `R$ ${parseFloat(faixa.limiteMaximo).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
                          </TableCell>
                          <TableCell className="py-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-red-600 hover:bg-red-50"
                              onClick={() => removerFaixaEtaria(faixa.id)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              
              {/* Idade máxima para fim da operação - Layout melhorado */}
              <div className="mt-4 pt-3 border-t border-purple-200 flex items-center gap-3">
                <Label className="text-sm text-purple-700 whitespace-nowrap">Idade máxima para fim da operação:</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    placeholder="80"
                    className="w-[70px] text-center"
                    value={novoParametro.idadeMaximaFimOperacao}
                    onChange={(e) => setNovoParametro({ ...novoParametro, idadeMaximaFimOperacao: e.target.value })}
                  />
                  <span className="text-sm text-muted-foreground">anos</span>
                </div>
              </div>
            </div>

            {/* Parcelas e Valores */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Parcelas Pagas (mín.)</Label>
                <Input
                  type="number"
                  placeholder="Ex: 6"
                  value={novoParametro.parcelasPagas}
                  onChange={(e) => setNovoParametro({ ...novoParametro, parcelasPagas: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Parcela Mínima (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Ex: 20.00"
                  value={novoParametro.valorParcelaMinima}
                  onChange={(e) => setNovoParametro({ ...novoParametro, valorParcelaMinima: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Parcela Máxima (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Ex: 5000.00"
                  value={novoParametro.valorParcelaMaxima}
                  onChange={(e) => setNovoParametro({ ...novoParametro, valorParcelaMaxima: e.target.value })}
                />
              </div>
            </div>

            {/* Saldo e Valores */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Saldo Devedor Mínimo (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Ex: 1000.00"
                  value={novoParametro.saldoDevedorMinimo}
                  onChange={(e) => setNovoParametro({ ...novoParametro, saldoDevedorMinimo: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Valor para Troco (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Ex: 100.00"
                  value={novoParametro.valorTroco}
                  onChange={(e) => setNovoParametro({ ...novoParametro, valorTroco: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Valor Contrato Mínima (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Ex: 0.00"
                  value={novoParametro.valorRCO}
                  onChange={(e) => setNovoParametro({ ...novoParametro, valorRCO: e.target.value })}
                />
              </div>
            </div>

            {/* Taxas de Juros */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Taxa Juros Mínima (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Ex: 1.50"
                  value={novoParametro.taxaJurosMin}
                  onChange={(e) => setNovoParametro({ ...novoParametro, taxaJurosMin: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Taxa Juros Máxima (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Ex: 2.14"
                  value={novoParametro.taxaJurosMax}
                  onChange={(e) => setNovoParametro({ ...novoParametro, taxaJurosMax: e.target.value })}
                />
              </div>
            </div>

            {/* Índices de Saldo Devedor */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <Label className="text-blue-700 mb-2 block">Índice de Saldo Devedor (por faixa de parcelas)</Label>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs">1 a 30 parcelas (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Ex: 32.00"
                    value={novoParametro.indiceSaldo1a30}
                    onChange={(e) => setNovoParametro({ ...novoParametro, indiceSaldo1a30: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">31 a 50 parcelas (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Ex: 30.00"
                    value={novoParametro.indiceSaldo31a50}
                    onChange={(e) => setNovoParametro({ ...novoParametro, indiceSaldo31a50: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">51 a 100 parcelas (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Ex: 28.00"
                    value={novoParametro.indiceSaldo51a100}
                    onChange={(e) => setNovoParametro({ ...novoParametro, indiceSaldo51a100: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Regras de Portabilidade - só aparece quando tipo é PORTABILIDADE */}
            {novoParametro.tipoOperacao === "PORTABILIDADE" && (
              <div className="space-y-4 p-4 bg-red-50 rounded-lg border border-red-200">
                <h4 className="font-semibold text-red-800 flex items-center gap-2">
                  <X className="h-4 w-4" />
                  Regras de Portabilidade por Banco
                </h4>
                
                {/* Bancos que NÃO PORTA */}
                <div className="space-y-2">
                  <Label className="text-red-700">Bancos que NÃO PORTA (não aceita portabilidade desses bancos)</Label>
                  <div className="flex gap-2">
                    <Select 
                      value={novoBancoNaoPorta} 
                      onValueChange={setNovoBancoNaoPorta}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Selecione o banco" />
                      </SelectTrigger>
                      <SelectContent>
                        {todosBancos
                          .filter(b => b.codigo !== banco.codigo) // Excluir o próprio banco
                          .filter(b => !novoParametro.bancosNaoPorta.includes(b.nome))
                          .map((b) => (
                            <SelectItem key={b.codigo} value={b.nome}>
                              {b.codigo} - {b.nome}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      type="button" 
                      size="sm" 
                      onClick={adicionarBancoNaoPorta}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {/* Lista de bancos que não porta */}
                  {novoParametro.bancosNaoPorta.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {novoParametro.bancosNaoPorta.map((bancoNome) => (
                        <Badge 
                          key={bancoNome} 
                          variant="destructive" 
                          className="gap-1 px-2 py-1"
                        >
                          {bancoNome}
                          <X 
                            className="h-3 w-3 cursor-pointer hover:scale-110" 
                            onClick={() => removerBancoNaoPorta(bancoNome)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Bancos que PORTA com regras específicas */}
                <div className="space-y-2">
                  <Label className="text-emerald-700">Bancos que PORTA com parcelas mínimas específicas</Label>
                  <div className="flex gap-2">
                    <Select 
                      value={novoBancoPorta.banco} 
                      onValueChange={(v) => setNovoBancoPorta(prev => ({ ...prev, banco: v }))}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Selecione o banco" />
                      </SelectTrigger>
                      <SelectContent>
                        {todosBancos
                          .filter(b => b.codigo !== banco.codigo) // Excluir o próprio banco
                          .filter(b => !novoParametro.bancosNaoPorta.includes(b.nome))
                          .filter(b => !novoParametro.bancosPortaRegras.some(r => r.banco === b.nome))
                          .map((b) => (
                            <SelectItem key={b.codigo} value={b.nome}>
                              {b.codigo} - {b.nome}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      placeholder="Parcelas"
                      className="w-24"
                      value={novoBancoPorta.parcelasPagas}
                      onChange={(e) => setNovoBancoPorta(prev => ({ ...prev, parcelasPagas: e.target.value }))}
                    />
                    <Button 
                      type="button" 
                      size="sm" 
                      onClick={adicionarBancoPortaRegra}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {/* Lista de bancos com regras */}
                  {novoParametro.bancosPortaRegras.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {novoParametro.bancosPortaRegras.map((regra) => (
                        <Badge 
                          key={regra.banco} 
                          className="gap-1 px-2 py-1 bg-emerald-600"
                        >
                          {regra.banco}: {regra.parcelasPagas} parc.
                          <X 
                            className="h-3 w-3 cursor-pointer hover:scale-110" 
                            onClick={() => removerBancoPortaRegra(regra.banco)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Parcelas pagas default para demais bancos */}
                <div className="space-y-2">
                  <Label className="text-blue-700">Parcelas pagas mínimas para demais bancos (não listados)</Label>
                  <Input
                    type="number"
                    placeholder="Ex: 1"
                    className="w-32"
                    value={novoParametro.parcelasPagasDefault}
                    onChange={(e) => setNovoParametro({ ...novoParametro, parcelasPagasDefault: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Bancos não listados acima terão esta quantidade mínima de parcelas pagas
                  </p>
                </div>
              </div>
            )}

            {/* Observações */}
            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea
                placeholder="Informações adicionais sobre este parâmetro..."
                value={novoParametro.observacoes}
                onChange={(e) => setNovoParametro({ ...novoParametro, observacoes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              limparFormParametro();
              setNovoParametroDialogOpen(false);
            }}>
              Cancelar
            </Button>
            <Button onClick={salvarParametro} className="bg-amber-600 hover:bg-amber-700">
              {editandoParametro ? "Salvar Alterações" : "Adicionar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Importar Comissões via Excel */}
      <Dialog open={importarComissaoDialogOpen} onOpenChange={setImportarComissaoDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-emerald-600" />
              Importar Tabela de Comissão
            </DialogTitle>
            <DialogDescription>
              Selecione um arquivo Excel com a tabela de comissão
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome da Tabela *</Label>
              <Input
                placeholder="Ex: Tabela Consignado 2025"
                value={nomeTabela}
                onChange={(e) => setNomeTabela(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Banco</Label>
                <Input
                  value={banco.nome}
                  disabled
                  className="bg-slate-100"
                />
              </div>
              <div className="space-y-2">
                <Label>Instituição (opcional)</Label>
                <Select value={instituicaoImport || "geral"} onValueChange={(v) => setInstituicaoImport(v === "geral" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a instituição" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="geral">Todas / Geral</SelectItem>
                    {instituicoesVinculadas.length > 0 ? (
                      instituicoesVinculadas.map(instCodigo => {
                        const inst = todasInstituicoes.find(i => i.codigo === instCodigo);
                        return (
                          <SelectItem key={instCodigo} value={instCodigo}>
                            {instCodigo} - {inst?.nome || instCodigo}
                          </SelectItem>
                        );
                      })
                    ) : (
                      todasInstituicoes.slice(0, 20).map(inst => (
                        <SelectItem key={inst.codigo} value={inst.codigo}>
                          {inst.codigo} - {inst.nome}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Arquivo Excel *</Label>
              <div 
                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-emerald-400 transition-colors bg-slate-50"
                onClick={() => document.getElementById('file-input-comissao').click()}
              >
                <input
                  id="file-input-comissao"
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => setArquivoComissao(e.target.files[0])}
                />
                {arquivoComissao ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileSpreadsheet className="h-10 w-10 text-green-600" />
                    <div className="text-left">
                      <span className="text-sm text-green-600 font-semibold block">{arquivoComissao.name}</span>
                      <span className="text-xs text-slate-500">Clique para trocar o arquivo</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Upload className="h-12 w-12 mx-auto text-emerald-400 mb-3" />
                    <p className="text-sm text-slate-600 font-medium">Clique para selecionar arquivo</p>
                    <p className="text-xs text-slate-400 mt-1">Formatos aceitos: .xlsx ou .xls</p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-xs text-emerald-700">
                <strong>Colunas esperadas:</strong> Banco Convênio, Tabela Produto, Comissão (%)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportarComissaoDialogOpen(false)} disabled={importandoComissao}>
              Cancelar
            </Button>
            <Button 
              onClick={importarComissoesExcel} 
              disabled={importandoComissao || !arquivoComissao || !nomeTabela.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {importandoComissao ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Importar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
